import { isSameDay, isAfter } from 'date-fns';
import React, { useEffect, useMemo, useState } from 'react';
import DatePicker from 'react-datepicker';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  discountTypesInfo,
  limitTypesList,
  discountTypesList,
  isActiveOptionsList,
  DiscountLimitType,
  DiscountType,
  isAllModuleAccessUser,
  showReactivationDiscounts,
} from './constants';
import { DuplicateDiscountPayload, Option } from './types';
import { axios } from '../api';
import { Pencil } from '../assets/Pencil';
import { Checkbox } from '../component/Checkbox';
import { ConfirmationModal } from '../component/ConfirmationModal';
import { Dropdown } from '../component/Dropdown';
import { TextInput } from '../component/TextInput';
import { LargeTooltip } from '../component/Tooltip';
import { TransferList } from '../component/TransferList';
import { Button } from '../component/buttons/Button';
import 'react-datepicker/dist/react-datepicker.css';
import {
  booleanOptions,
  countryOptions,
  countryToCurrency,
  currencyOptions,
} from '../dropdownOptions';
import { BOTTOM_TOAST, formatDateTime, generateErrorMsg } from '../utils';

const parseDateString = (dateString?: string | null): Date | undefined => {
  if (!dateString) return undefined;
  const parsed = new Date(dateString);
  return !Number.isNaN(parsed.getTime()) ? parsed : undefined;
};

export const CreateDiscount = () => {
  const discountTypes = useMemo(() => discountTypesList, []);
  const isActiveOptions = useMemo(() => isActiveOptionsList, []);
  const limitTypes = useMemo(() => limitTypesList, []);

  const location = useLocation();
  const duplicateDiscount = location.state?.duplicateDiscount as DuplicateDiscountPayload;
  const [code, setCode] = useState('');
  const [type, setType] = useState(
    () =>
      discountTypes.find((opt) => opt.value === duplicateDiscount?.discount_type) ??
      discountTypes[0]
  );
  const [value, setValue] = useState(() => Number(duplicateDiscount?.value ?? 0));
  const [trialValue, setTrialValue] = useState(() => Number(duplicateDiscount?.trial_value ?? 0));
  const [maxRedemptionAmount, setMaxRedemptionAmount] = useState(() =>
    duplicateDiscount?.max_redemption_amount != null
      ? String(duplicateDiscount.max_redemption_amount)
      : ''
  );
  const [numberOfCodes, setNumberOfCodes] = useState(1);
  const [description, setDescription] = useState(() => duplicateDiscount?.description ?? '');
  const [limitType, setLimitType] = useState<any>(() =>
    limitTypes.find((opt) => opt.value === duplicateDiscount?.limit_type)
  );
  const [limitValue, setLimitValue] = useState(() => Number(duplicateDiscount?.limit_value ?? 0));
  const [startDate, setStartDate] = useState<any>(() =>
    parseDateString(duplicateDiscount?.available_start_date)
  );
  const [endDate, setEndDate] = useState<any>(() =>
    parseDateString(duplicateDiscount?.available_end_date)
  );
  const [isActive, setIsActive] = useState(() => {
    if (!duplicateDiscount) return isActiveOptions[0];
    return (
      isActiveOptions.find(
        (opt) => opt.value === (duplicateDiscount.is_active ? 'true' : 'false')
      ) ?? isActiveOptions[0]
    );
  });
  const [trial, setTrial] = useState(() => Boolean(duplicateDiscount?.order_trial));
  const [subscription, setSubcription] = useState(() =>
    Boolean(duplicateDiscount?.order_subscription)
  );
  const [onetime, setOnetime] = useState(() => Boolean(duplicateDiscount?.order_onetime));
  const [reactivationOnly, setReactivationOnly] = useState(
    () => showReactivationDiscounts() && Boolean(duplicateDiscount?.reactivation_only)
  );
  const [availableProducts, setAvailableProducts] = useState<string[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<string[]>(
    () => duplicateDiscount?.eligible_products ?? []
  );
  const [endDateError, setEndDateError] = useState('');
  const [validationErrors, setValidationErrors] = useState<any>(null);
  const [exemptFromSaleState, setExemptFromSaleState] = useState(() => {
    if (!duplicateDiscount) return { label: 'No', value: 'false' };
    return (
      booleanOptions.find(
        (opt) => opt.value === (duplicateDiscount.exempt_from_sale_state ? 'true' : 'false')
      ) ?? { label: 'No', value: 'false' }
    );
  });
  const [showSelectEligibleProducts, setShowSelectEligibleProducts] = useState(
    () => (duplicateDiscount?.eligible_products?.length ?? 0) > 0
  );
  const [showConfirmCreateDiscountModal, setShowConfirmCreateDiscountModal] = useState(false);
  const [eligibleCountry, setEligibleCountry] = useState<Option | undefined>(() =>
    countryOptions.find((opt) => opt.value === duplicateDiscount?.eligible_country)
  );
  const [currency, setCurrency] = useState<Option | undefined>(() =>
    currencyOptions.find((opt) => opt.value === duplicateDiscount?.currency)
  );
  const [isBulk, setIsBulk] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const isBulkMode = isAllModuleAccessUser() && isBulk;

  const changeLimitType = (newSelection: any) => {
    if (newSelection?.value === DiscountLimitType.X_ORDERS_PER_CUSTOMER_SUBSCRIPTION_TRIAL_SPLIT) {
      setTrial(true);
      setSubcription(true);
      setOnetime(false);
      setReactivationOnly(false);
    }
    setLimitType(newSelection);
  };

  const changeReactivationOnly = (isChecked: boolean) => {
    if (isChecked) {
      setTrial(false);
      setSubcription(false);
      setOnetime(false);
    }
    setReactivationOnly(isChecked);
  };

  const requiredFieldsAreValid =
    endDateError.length === 0 &&
    code &&
    type &&
    value &&
    limitType &&
    (limitType.value !== DiscountLimitType.X_ORDERS_PER_CUSTOMER_SUBSCRIPTION_TRIAL_SPLIT ||
      trialValue) &&
    (!limitType.needsValue || limitValue) &&
    (!(startDate || endDate) || (startDate && endDate)) &&
    (trial || subscription || onetime || reactivationOnly) &&
    currency &&
    eligibleCountry;

  const confirmationModalText = {
    title: `Are you sure you want to create ${isBulkMode ? 'these' : 'this'} new discount code${
      isBulkMode ? 's' : ''
    }?`,
    message: `Please ensure all fields are correct before proceeding, as they are not editable after the discount${
      isBulkMode ? 's' : ''
    } is created.`,
    confirmLabel: `Create Discount${isBulkMode ? 's' : ''}`,
  };

  const isTrialOnly = trial && !subscription && !onetime;
  const isTrialAmountDiscount = trial && type.value === DiscountType.AMOUNT_DISCOUNT;
  const showMaxRedemptionAmount =
    type.value === DiscountType.PERCENTAGE_DISCOUNT &&
    limitType?.value !== DiscountLimitType.X_ORDERS_PER_CUSTOMER_SUBSCRIPTION_TRIAL_SPLIT;

  useEffect(() => {
    if (isTrialAmountDiscount) {
      setShowSelectEligibleProducts(false);
      setSelectedProducts([]);
    }
  }, [isTrialAmountDiscount]);

  useEffect(() => {
    if (!showMaxRedemptionAmount) {
      setMaxRedemptionAmount('');
    }
  }, [showMaxRedemptionAmount]);

  useEffect(() => {
    if (!isTrialOnly) {
      setExemptFromSaleState(
        booleanOptions.find((opt) => opt.value === 'true') ?? { label: 'Yes', value: 'true' }
      );
    }
  }, [isTrialOnly]);

  useEffect(() => {
    axios
      .get(`products/discount-eligible-codes`)
      .then((response) => {
        const products: string[] = response.data ?? [];
        const eligibleProducts = duplicateDiscount?.eligible_products;
        if (eligibleProducts?.length) {
          setAvailableProducts(products.filter((product) => !eligibleProducts.includes(product)));
        } else {
          setAvailableProducts(products);
        }
      })
      .catch((error) => console.error('Error:', error));
  }, [duplicateDiscount]);

  const handleCodeChange = (value: string) => {
    setCode(value.toUpperCase());
  };

  const handleCountryChange = (option: Option) => {
    setEligibleCountry(option);
    const currencyOption = currencyOptions.find((c) => c.value === countryToCurrency[option.value]);
    setCurrency(currencyOption);
  };

  const handleCurrencyChange = (option: Option) => {
    setCurrency(option);
    const countryOption = countryOptions.find((c) => countryToCurrency[c.value] === option.value);
    setEligibleCountry(countryOption);
  };

  const handleCreateDiscount = () => {
    // Block trial only codes + unlimited combination
    const isTrialOnly = trial && !subscription && !onetime;

    if (
      isTrialOnly &&
      (limitType.value === DiscountLimitType.UNLIMITED ||
        (limitType.value === DiscountLimitType.X_ORDERS_FOR_JUST_ONE_CUSTOMER && limitValue > 1) ||
        (limitType.value === DiscountLimitType.X_ORDERS_PER_CUSTOMER && limitValue > 1))
    ) {
      setShowConfirmCreateDiscountModal(false);
      toast.error(
        'Trial-only codes cannot have a limit type more than 1 order per customer.',
        BOTTOM_TOAST
      );
      return;
    }

    let endpoint = 'discounts';
    if (isBulkMode) {
      endpoint = 'discounts/bulk_create';
    }

    const requestBody = {
      code,
      discount_type: type.value,
      value: Number(value),
      trial_value:
        limitType.value === DiscountLimitType.X_ORDERS_PER_CUSTOMER_SUBSCRIPTION_TRIAL_SPLIT
          ? Number(trialValue)
          : null,
      max_redemption_amount:
        showMaxRedemptionAmount && maxRedemptionAmount !== '' ? Number(maxRedemptionAmount) : null,
      description,
      limit_type: limitType.value,
      limit_value: limitType.needsValue ? Number(limitValue) : null,
      available_start_date: startDate,
      available_end_date: endDate,
      is_active: isActive.value === 'true',
      order_trial: trial,
      order_subscription: subscription,
      order_onetime: onetime,
      reactivation_only: reactivationOnly,
      eligible_products: isTrialAmountDiscount ? [] : selectedProducts,
      exempt_from_sale_state: exemptFromSaleState.value === 'true',
      number_of_codes: numberOfCodes,
      eligible_country: eligibleCountry?.value,
      currency: currency?.value,
    };

    setIsLoading(true);
    axios
      .post(endpoint, requestBody)
      .then((_) => {
        setIsLoading(false);
        navigate('/discounts');
        let successMessage = `Successfully created discount code ${code}`;
        if (isBulkMode) {
          successMessage = `Successfully started bulk creation of ${numberOfCodes} discounts starting with ${code}`;
        }
        toast.success(successMessage, BOTTOM_TOAST);
      })
      .catch((e) => {
        setIsLoading(false);
        setValidationErrors(e.response.data);
        setShowConfirmCreateDiscountModal(false);
        toast.error(generateErrorMsg(e), BOTTOM_TOAST);
      });
  };

  return (
    <div className="flex justify-center items-center bg-slate50 p-8">
      <div className="flex flex-col bg-lightGray rounded-lg shadow-md p-4 w-[750px] relative border font-semibold">
        <div className="flex flex-row mb-4 justify-between w-full">
          <h1 className="text-left">Create new discount</h1>
          {isAllModuleAccessUser() ? (
            <Checkbox
              label="Create in Bulk"
              isChecked={isBulk}
              onChange={(isBulk) => setIsBulk(isBulk)}
              disabled={!isAllModuleAccessUser()}
            />
          ) : null}
        </div>
        {isBulkMode ? (
          <div className="bg-white rounded-lg shadow-md p-4 min-w-[550px] relative border">
            <div className="w-full pr-2">
              <h2>Number of Codes</h2>
              <p className="text-xs text-gray-500">
                Number of unique discount codes that will be automatically generated with the same
                settings.
              </p>
              <div className="w-full">
                <TextInput
                  placeholder="Enter value"
                  value={numberOfCodes.toString()}
                  type="number"
                  setTextChange={(value) => setNumberOfCodes(Number(value))}
                  error={validationErrors?.numberOfCodes}
                />
              </div>
            </div>
          </div>
        ) : null}
        <div className="bg-white mt-4 mb-4 rounded-lg shadow-md p-4 min-w-[550px] relative border padd">
          <div className="text-md text-left">
            <div className="w-full mb-2">
              <h2>{`Discount Code${isBulkMode ? ' Prefix' : ''}`}</h2>
              {isBulkMode ? (
                <p className="text-xs text-gray-500 font-normal">
                  Characters that will appear at the start of each discount code created in bulk
                </p>
              ) : null}
              <TextInput
                placeholder="Enter code"
                value={code}
                setTextChange={(value) => handleCodeChange(value)}
                bottomText="Customer-facing discount code (e.g. WELCOME20)"
                error={validationErrors?.code}
              />
            </div>

            <div className="w-full mb-2">
              <h2>
                Description <span className="text-xs">(Optional)</span>
              </h2>
              <TextInput
                placeholder="Enter description"
                value={description}
                setTextChange={setDescription}
                bottomText="Internal description of discount code"
              />
            </div>

            <div className="w-full mb-2 {isActive.className}">
              <h2>Status</h2>
              <Dropdown
                options={isActiveOptions}
                selectedOption={isActive}
                setSelectedOption={(newSelection) => setIsActive(newSelection)}
              />
            </div>
          </div>
        </div>
        <div className="bg-white mb-4 rounded-lg shadow-md p-4 min-w-[550px] relative border">
          <h2>
            Active dates <span className="text-xs">(Optional)</span>
          </h2>
          <p className="text-xs text-gray-500 font-normal">
            The customer will only receive the discount if applied within this time frame
          </p>

          <div className="flex mt-2">
            <div className="w-1/2 pr-2">
              <h3>Start Date</h3>
              <div className="w-full mb-2">
                <DatePicker
                  selected={startDate}
                  minDate={new Date()}
                  onChange={(date) => {
                    setStartDate(date);
                    if (startDate && !endDate) {
                      setEndDateError('End date is required');
                    } else if (date && endDate && date >= endDate) {
                      setEndDateError('End time must be later than start time');
                    } else {
                      setEndDateError('');
                    }
                  }}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={30}
                  dateFormat="dd/MM/yyyy"
                  customInput={
                    <div className="flex items-center cursor-pointer">
                      <span>{startDate ? formatDateTime(startDate, true) : 'Select Date'}</span>
                      <Pencil className="w-4 h-4 ml-1 mb-1" />
                    </div>
                  }
                />
              </div>
            </div>
            <div className="w-1/2">
              <h3>End Date</h3>
              <div className="w-full mb-2">
                <DatePicker
                  key={startDate?.toString()}
                  selected={endDate || startDate}
                  minDate={startDate || new Date()}
                  filterTime={(time) => {
                    if (startDate && isSameDay(time, startDate)) {
                      return isAfter(time, startDate);
                    }
                    return true;
                  }}
                  onChange={(date) => {
                    setEndDate(date);
                    if (date && date <= startDate) {
                      setEndDateError('End time must be later than start time');
                    } else {
                      setEndDateError('');
                    }
                  }}
                  dateFormat="dd/MM/yyyy"
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={30}
                  customInput={
                    <div className="flex items-center cursor-pointer">
                      <span>{endDate ? formatDateTime(endDate, true) : 'Select Date'}</span>
                      <Pencil className="w-4 h-4 ml-1 mb-1" />
                    </div>
                  }
                />
                {endDateError && (
                  <div className="error text-red-500 text-xs font-normal">{endDateError}</div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white mb-4 rounded-lg shadow-md p-4 min-w-[550px] relative border">
          <div className="w-full pr-2 mb-2">
            <h2 className="flex">
              Discount limit type{' '}
              <span className="px-2">
                <LargeTooltip textArray={discountTypesInfo} />
              </span>
            </h2>
            <Dropdown
              options={limitTypes}
              selectedOption={limitType}
              setSelectedOption={(newSelection) => changeLimitType(newSelection)}
              noDefaultSelection
              defaultText="Select limit type"
            />
          </div>
          {limitType?.needsValue ? (
            <>
              <h2>Discount limit</h2>
              {limitType?.value ===
                DiscountLimitType.X_ORDERS_PER_CUSTOMER_SUBSCRIPTION_TRIAL_SPLIT && (
                <div className="flex">
                  <div className="w-1/4 mb-2">
                    <div>
                      <TextInput
                        placeholder="Enter value"
                        value="1"
                        setTextChange={(value) => setLimitValue(Number(value))}
                        type="number"
                        disabled
                        error={validationErrors?.limit_value}
                      />
                    </div>
                  </div>
                  <div className="w-3/4 items-center flex">
                    <span className="ml-2">Trial Order</span>
                  </div>
                </div>
              )}

              <div className="flex">
                <div className="w-1/4">
                  <div>
                    <TextInput
                      placeholder="Enter value"
                      value={limitValue.toString()}
                      setTextChange={(value) => setLimitValue(Number(value))}
                      type="number"
                      error={validationErrors?.limit_value}
                    />
                  </div>
                </div>
                <div className="w-3/4 items-center flex">
                  <span className="ml-2">{limitType?.valueText ?? ''}</span>
                </div>
              </div>
              <p className="mt-1 text-xs text-gray font-normal">
                {limitType?.valueBottomText ?? ''}
              </p>
            </>
          ) : null}
        </div>

        <div className="bg-white mb-4 rounded-lg shadow-md p-4 min-w-[550px] relative border">
          <h2>Discount value</h2>
          {limitType?.value === DiscountLimitType.X_ORDERS_PER_CUSTOMER_SUBSCRIPTION_TRIAL_SPLIT ? (
            <>
              <div className="flex">
                <div className="w-2/5 mb-2 pr-2">
                  <div className="w-full mb-2">
                    <Dropdown
                      options={discountTypes}
                      selectedOption={discountTypes[0]}
                      setSelectedOption={(newSelection) => setType(newSelection)}
                      isDisabled
                    />
                  </div>
                </div>
                <div className="w-1/5">
                  <div className="w-full mb-2">
                    <TextInput
                      placeholder="Enter value"
                      value={trialValue.toString()}
                      type="number"
                      setTextChange={(value) => setTrialValue(Number(value))}
                      error={validationErrors?.trial_value}
                    />
                  </div>
                </div>
                <div className="w-2/5 items-center flex">
                  <span className="ml-2">off of Trial Order</span>
                </div>
              </div>

              <div className="flex">
                <div className="w-2/5 mb-2 pr-2">
                  <div className="w-full mb-2">
                    <Dropdown
                      options={discountTypes}
                      selectedOption={discountTypes[0]}
                      setSelectedOption={(newSelection) => setType(newSelection)}
                      isDisabled
                    />
                  </div>
                </div>
                <div className="w-1/5">
                  <div className="w-full mb-2">
                    <TextInput
                      placeholder="Enter value"
                      value={value.toString()}
                      type="number"
                      setTextChange={(value) => setValue(Number(value))}
                      error={validationErrors?.value}
                    />
                  </div>
                </div>
                <div className="w-2/5 items-center flex">
                  <span className="ml-2">off of Subscription Order(s)</span>
                </div>
              </div>
            </>
          ) : (
            <div className="flex">
              <div className="w-3/5 mb-2 pr-2">
                <div className="w-full mb-2">
                  <Dropdown
                    options={discountTypes}
                    selectedOption={type}
                    setSelectedOption={(newSelection) => setType(newSelection)}
                  />
                </div>
              </div>

              <div className="w-2/5">
                <div className="w-full mb-2">
                  <TextInput
                    placeholder="Enter value"
                    value={value.toString()}
                    type="number"
                    setTextChange={(value) => setValue(Number(value))}
                    error={validationErrors?.value}
                  />
                </div>
              </div>
            </div>
          )}
          {showMaxRedemptionAmount && (
            <div className="w-full mb-2">
              <h2>
                Max redemption amount limit <span className="text-xs">(Optional)</span>
              </h2>
              <p className="mb-2 text-xs text-gray font-normal">
                Caps the dollar amount of this discount per order. Leave blank for no limit.
              </p>
              <div className="w-1/4 mb-2">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <TextInput
                    value={maxRedemptionAmount}
                    type="number"
                    className="pl-6"
                    setTextChange={(value) => setMaxRedemptionAmount(value)}
                    onKeyDown={(event) => {
                      if (['e', 'E', '+', '-'].includes(event.key)) {
                        event.preventDefault();
                      }
                    }}
                  />
                </div>
                {validationErrors?.max_redemption_amount && (
                  <p className="text-red-500 text-xs mt-1 font-normal w-full overflow-auto">
                    {validationErrors.max_redemption_amount}
                  </p>
                )}
              </div>
            </div>
          )}
          <div className="flex">
            <div className="mb-2 mr-2 flex-1">
              <h2 className="mb-2">Currency</h2>
              <Dropdown
                noDefaultSelection
                options={currencyOptions}
                selectedOption={currency}
                setSelectedOption={handleCurrencyChange}
              />
            </div>
            <div className="mb-2 mr-2 flex-1">
              <h2 className="mb-2">Eligible country</h2>
              <Dropdown
                noDefaultSelection
                options={countryOptions}
                selectedOption={eligibleCountry}
                setSelectedOption={handleCountryChange}
              />
            </div>
          </div>
          <div className="mb-2">
            <h2 className="mb-2">Eligible order types</h2>
            <>
              <Checkbox
                label="Trial"
                isChecked={trial}
                onChange={(isChecked) => setTrial(isChecked)}
                disabled={
                  limitType?.value ===
                    DiscountLimitType.X_ORDERS_PER_CUSTOMER_SUBSCRIPTION_TRIAL_SPLIT ||
                  reactivationOnly
                }
              />
              <Checkbox
                label="Subscription"
                isChecked={subscription}
                onChange={(isChecked) => setSubcription(isChecked)}
                disabled={
                  limitType?.value ===
                    DiscountLimitType.X_ORDERS_PER_CUSTOMER_SUBSCRIPTION_TRIAL_SPLIT ||
                  reactivationOnly
                }
              />
              <Checkbox
                label="Onetime"
                isChecked={onetime}
                onChange={(isChecked) => setOnetime(isChecked)}
                disabled={
                  limitType?.value ===
                    DiscountLimitType.X_ORDERS_PER_CUSTOMER_SUBSCRIPTION_TRIAL_SPLIT ||
                  reactivationOnly
                }
              />
              {showReactivationDiscounts() && (
                <Checkbox
                  label="Reactivation"
                  isChecked={reactivationOnly}
                  onChange={changeReactivationOnly}
                  disabled={
                    limitType?.value ===
                    DiscountLimitType.X_ORDERS_PER_CUSTOMER_SUBSCRIPTION_TRIAL_SPLIT
                  }
                />
              )}
            </>
          </div>
          <h2>Eligible products</h2>
          <p>
            Should this discount be applicable to all products or just a specific list of products?
          </p>
          <div className={`py-2 ${isTrialAmountDiscount ? 'pointer-events-none' : ''}`}>
            <input
              type="radio"
              id="option1"
              name="option"
              value="Option 1"
              checked={showSelectEligibleProducts === false}
              onChange={() => {
                setShowSelectEligibleProducts(false);
                setSelectedProducts([]);
              }}
            />
            <label htmlFor="option1" className="pb-2">
              All products
              <p className="text-xs text-gray-500 font-normal">
                Discount will be added to all products, including any new products we add in the
                future
              </p>
            </label>
            <input
              type="radio"
              id="option2"
              name="option"
              value="Option 2"
              checked={showSelectEligibleProducts === true}
              onChange={() => setShowSelectEligibleProducts(true)}
            />
            <label htmlFor="option2" className="inline-flex flex-col gap-1">
              <span className={isTrialAmountDiscount ? 'text-[#808080]' : ''}>Select products</span>
              <p
                className={`text-xs font-normal ${
                  isTrialAmountDiscount ? 'text-[#585858]' : 'text-gray-500'
                }`}
              >
                Discount will only be applicable to select list of products
              </p>
            </label>
          </div>
          {showSelectEligibleProducts && (
            <TransferList
              availableProducts={availableProducts}
              setAvailableProducts={setAvailableProducts}
              selectedProducts={selectedProducts}
              setSelectedProducts={setSelectedProducts}
            />
          )}
        </div>

        <div className="bg-white mb-4 rounded-lg shadow-md p-4 min-w-[550px] relative border">
          <div className="w-full pr-2 mb-2">
            <h2 className="flex">Eligibility during a sale</h2>
            <p className="text-xs text-gray-500">
              Will this discount be exempt from override during a sitewide sale?
            </p>
            <Dropdown
              options={booleanOptions}
              selectedOption={exemptFromSaleState}
              setSelectedOption={(newSelection) => setExemptFromSaleState(newSelection)}
              isDisabled={!isTrialOnly}
            />
          </div>
        </div>

        <div className="flex justify-center flex-col items-center mt-2">
          <Button
            variant={requiredFieldsAreValid ? 'primary' : 'secondary'}
            label={`Create Discount${isBulkMode ? 's' : ''}`}
            isDisabled={!requiredFieldsAreValid || isLoading}
            onClick={() => setShowConfirmCreateDiscountModal(true)}
          />
        </div>

        {showConfirmCreateDiscountModal && (
          <ConfirmationModal
            {...confirmationModalText}
            onConfirm={() => handleCreateDiscount()}
            onCancel={() => setShowConfirmCreateDiscountModal(false)}
          />
        )}
      </div>
    </div>
  );
};
